import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Copy,
  Filter,
  GripVertical,
  MapPin,
  Maximize2,
  Minimize2,
  Minus,
  MoreVertical,
  Pencil,
  Plus,
  Palette,
  RefreshCw,
  Repeat2,
  Search,
  Trash2,
  UserRound,
  Users,
  Send,
  Sparkles,
  Route,
  X,
} from "lucide-react";

import api from "../lib/api";

import StaffPlannerView from "../components/workforce/StaffPlannerView";

// PROJECT_ATLAS_WORKFORCE_PLANNER_V40A
// PROJECT_ATLAS_WORKFORCE_PLANNER_V40C
// PROJECT_ATLAS_WORKFORCE_PLANNER_V40D

const STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "upcoming", label: "Upcoming" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

const SERVICE_TYPES = [
  "Personal Care",
  "Community Access",
  "Domestic Assistance",
  "Transport",
  "Social Support",
  "Meal Preparation",
  "Medication Support",
  "Respite",
  "Supported Independent Living",
  "Other",
];

const STATUS_STYLE = {
  draft: {
    badge: "border-violet-200 bg-violet-50 text-violet-800",
    card: "border-l-violet-500",
    dot: "bg-violet-500",
  },
  upcoming: {
    badge: "border-amber-200 bg-amber-50 text-amber-800",
    card: "border-l-amber-500",
    dot: "bg-amber-500",
  },
  in_progress: {
    badge: "border-blue-200 bg-blue-50 text-blue-800",
    card: "border-l-blue-500",
    dot: "bg-blue-500",
  },
  completed: {
    badge: "border-emerald-200 bg-emerald-50 text-emerald-800",
    card: "border-l-emerald-500",
    dot: "bg-emerald-500",
  },
  cancelled: {
    badge: "border-red-200 bg-red-50 text-red-800",
    card: "border-l-red-500",
    dot: "bg-red-500",
  },
};

const SERVICE_CARD_STYLE = {
  "Personal Care": {
    card: "border-rose-200 bg-gradient-to-br from-rose-50 via-white to-pink-50",
    header: "text-rose-950",
    badge: "bg-rose-100 text-rose-800 ring-1 ring-rose-200",
    accent: "bg-rose-500",
  },
  "Community Access": {
    card: "border-sky-200 bg-gradient-to-br from-sky-50 via-white to-cyan-50",
    header: "text-sky-950",
    badge: "bg-sky-100 text-sky-800 ring-1 ring-sky-200",
    accent: "bg-sky-500",
  },
  "Domestic Assistance": {
    card: "border-amber-200 bg-gradient-to-br from-amber-50 via-white to-yellow-50",
    header: "text-amber-950",
    badge: "bg-amber-100 text-amber-800 ring-1 ring-amber-200",
    accent: "bg-amber-500",
  },
  Transport: {
    card: "border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-blue-50",
    header: "text-indigo-950",
    badge: "bg-indigo-100 text-indigo-800 ring-1 ring-indigo-200",
    accent: "bg-indigo-500",
  },
  "Social Support": {
    card: "border-fuchsia-200 bg-gradient-to-br from-fuchsia-50 via-white to-purple-50",
    header: "text-fuchsia-950",
    badge: "bg-fuchsia-100 text-fuchsia-800 ring-1 ring-fuchsia-200",
    accent: "bg-fuchsia-500",
  },
  "Meal Preparation": {
    card: "border-orange-200 bg-gradient-to-br from-orange-50 via-white to-amber-50",
    header: "text-orange-950",
    badge: "bg-orange-100 text-orange-800 ring-1 ring-orange-200",
    accent: "bg-orange-500",
  },
  "Medication Support": {
    card: "border-teal-200 bg-gradient-to-br from-teal-50 via-white to-emerald-50",
    header: "text-teal-950",
    badge: "bg-teal-100 text-teal-800 ring-1 ring-teal-200",
    accent: "bg-teal-500",
  },
  Respite: {
    card: "border-violet-200 bg-gradient-to-br from-violet-50 via-white to-purple-50",
    header: "text-violet-950",
    badge: "bg-violet-100 text-violet-800 ring-1 ring-violet-200",
    accent: "bg-violet-500",
  },
  "Supported Independent Living": {
    card: "border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-green-50",
    header: "text-emerald-950",
    badge: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200",
    accent: "bg-emerald-500",
  },
  Other: {
    card: "border-slate-200 bg-gradient-to-br from-slate-50 via-white to-zinc-50",
    header: "text-slate-950",
    badge: "bg-slate-100 text-slate-800 ring-1 ring-slate-200",
    accent: "bg-slate-500",
  },
};

const DEFAULT_SERVICE_CARD_STYLE = SERVICE_CARD_STYLE.Other;

const WEEKDAY_OPTIONS = [
  { value: 0, label: "Mon" },
  { value: 1, label: "Tue" },
  { value: 2, label: "Wed" },
  { value: 3, label: "Thu" },
  { value: 4, label: "Fri" },
  { value: 5, label: "Sat" },
  { value: 6, label: "Sun" },
];

const EMPTY_FORM = {
  id: null,
  client_id: "",
  staff_id: "",
  date: "",
  start_time: "09:00",
  end_time: "10:00",
  service_type: "",
  address: "",
  notes: "",
  geofence_radius_m: 100,
  status: "draft",
  recurring_series_id: null,
  recurring_occurrence_date: null,
  edit_scope: "this",
  recurrence_type: "none",
  recurrence_weekdays: [],
  recurrence_end_mode: "never",
  recurrence_end_date: "",
  generate_weeks: 12,
};

function pad(value) {
  return String(value).padStart(2, "0");
}

function localDateString(date) {
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
  ].join("-");
}

function parseLocalDate(value) {
  const [year, month, day] = String(value)
    .split("-")
    .map(Number);

  return new Date(year, month - 1, day);
}

function weekdayIndexFromDate(value) {
  if (!value) {
    return null;
  }

  const date = parseLocalDate(value);
  const day = date.getDay();

  return day === 0 ? 6 : day - 1;
}

function recurrenceLabel(value) {
  if (value === "weekly") {
    return "Weekly";
  }

  if (value === "fortnightly") {
    return "Fortnightly";
  }

  return "One-off";
}

function startOfWeek(value) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);

  const day = date.getDay();
  const offset = day === 0 ? -6 : 1 - day;

  date.setDate(date.getDate() + offset);
  return date;
}

function addDays(value, days) {
  const date = new Date(value);
  date.setDate(date.getDate() + days);
  return date;
}

function addWeeks(value, weeks) {
  return addDays(value, weeks * 7);
}

function minutesFromTime(value) {
  if (!value || !String(value).includes(":")) {
    return null;
  }

  const [hours, minutes] = String(value)
    .slice(0, 5)
    .split(":")
    .map(Number);

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes)
  ) {
    return null;
  }

  return hours * 60 + minutes;
}

function timeFromMinutes(value) {
  const normalized =
    ((Number(value) % (24 * 60)) + 24 * 60) %
    (24 * 60);

  return `${pad(Math.floor(normalized / 60))}:${pad(
    normalized % 60
  )}`;
}

function shiftTime(value, minutes) {
  const base = minutesFromTime(value);

  if (base === null) {
    return value;
  }

  return timeFromMinutes(base + minutes);
}

function getServiceCardStyle(serviceType) {
  return (
    SERVICE_CARD_STYLE[serviceType] ||
    DEFAULT_SERVICE_CARD_STYLE
  );
}

function durationMinutes(startTime, endTime) {
  const start = minutesFromTime(startTime);
  const end = minutesFromTime(endTime);

  if (start === null || end === null) {
    return 0;
  }

  if (end > start) {
    return end - start;
  }

  // Overnight visit.
  if (end < start) {
    return 24 * 60 - start + end;
  }

  return 0;
}

function formatDuration(minutes) {
  if (!minutes) {
    return "0h";
  }

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;

  if (!remainder) {
    return `${hours}h`;
  }

  if (!hours) {
    return `${remainder}m`;
  }

  return `${hours}h ${remainder}m`;
}

function formatDayHeading(date) {
  return date.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function formatWeekRange(start) {
  const end = addDays(start, 6);

  const startText = start.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
  });

  const endText = end.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return `${startText} – ${endText}`;
}

function getStatusStyle(status) {
  return STATUS_STYLE[status] || STATUS_STYLE.upcoming;
}

function getStatusLabel(status) {
  return (
    STATUS_OPTIONS.find((option) => option.value === status)
      ?.label || "Upcoming"
  );
}

function normalizeListResponse(response) {
  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.items)) {
    return response.data.items;
  }

  if (Array.isArray(response?.data?.results)) {
    return response.data.results;
  }

  return [];
}

function getInitials(name, fallback = "?") {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) {
    return fallback;
  }

  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function getPersonName(person, fallback = "Unknown") {
  if (!person) {
    return fallback;
  }

  return (
    person.full_name ||
    person.name ||
    [person.first_name, person.last_name]
      .filter(Boolean)
      .join(" ") ||
    fallback
  );
}

function visitInterval(visit) {
  const start = minutesFromTime(visit.start_time);
  const end = minutesFromTime(visit.end_time);

  if (start === null || end === null) {
    return null;
  }

  return {
    start,
    end: end > start ? end : end + 24 * 60,
  };
}

function visitsOverlap(first, second) {
  const a = visitInterval(first);
  const b = visitInterval(second);

  if (!a || !b) {
    return false;
  }

  return a.start < b.end && b.start < a.end;
}

function RosterModal({
  open,
  form,
  setForm,
  participants,
  staff,
  visits,
  saving,
  onClose,
  onSubmit,
}) {
  const conflicts = useMemo(() => {
    if (
      !form.staff_id ||
      !form.date ||
      !form.start_time ||
      !form.end_time
    ) {
      return [];
    }

    return visits.filter((visit) => {
      if (visit.id === form.id) {
        return false;
      }

      if (visit.status === "cancelled") {
        return false;
      }

      if (visit.staff_id !== form.staff_id) {
        return false;
      }

      if (visit.date !== form.date) {
        return false;
      }

      return visitsOverlap(form, visit);
    });
  }, [form, visits]);

  if (!open) {
    return null;
  }

  const selectedParticipant = participants.find(
    (participant) => participant.id === form.client_id
  );

  const handleParticipantChange = (clientId) => {
    const participant = participants.find(
      (item) => item.id === clientId
    );

    setForm((current) => ({
      ...current,
      client_id: clientId,
      address:
        current.address ||
        participant?.address ||
        participant?.residential_address ||
        "",
    }));
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/50 p-3 backdrop-blur-sm">
      <div className="max-h-[95vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white text-slate-900 shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-5 py-4">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              {form.id ? "Edit rostered visit" : "Create rostered visit"}
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Assign a participant, worker, date and service time.
            </p>

            <p className="mt-1 text-xs font-medium text-emerald-700">
              Saving creates a Draft. Publish the day or week when the roster is ready.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-5 p-5">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-1.5">
              <span className="text-sm font-semibold text-slate-700">
                Participant
              </span>

              <select
                value={form.client_id}
                onChange={(event) =>
                  handleParticipantChange(event.target.value)
                }
                required
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-500"
              >
                <option value="">Select participant</option>

                {participants.map((participant) => (
                  <option
                    key={participant.id}
                    value={participant.id}
                  >
                    {getPersonName(participant, "Participant")}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-1.5">
              <span className="text-sm font-semibold text-slate-700">
                Support worker
              </span>

              <select
                value={form.staff_id}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    staff_id: event.target.value,
                  }))
                }
                required
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-500"
              >
                <option value="">Select worker</option>

                {staff.map((worker) => (
                  <option key={worker.id} value={worker.id}>
                    {getPersonName(worker, "Worker")}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <label className="space-y-1.5">
              <span className="text-sm font-semibold text-slate-700">
                Date
              </span>

              <input
                type="date"
                value={form.date}
                onChange={(event) => {
                  const date = event.target.value;
                  const weekday = weekdayIndexFromDate(date);

                  setForm((current) => ({
                    ...current,
                    date,
                    recurrence_weekdays:
                      current.recurrence_type === "none" ||
                      weekday === null
                        ? current.recurrence_weekdays
                        : [weekday],
                  }));
                }}
                required
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-500"
              />
            </label>

            <label className="space-y-1.5">
              <span className="text-sm font-semibold text-slate-700">
                Start time
              </span>

              <input
                type="time"
                value={form.start_time}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    start_time: event.target.value,
                  }))
                }
                required
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-500"
              />
            </label>

            <label className="space-y-1.5">
              <span className="text-sm font-semibold text-slate-700">
                End time
              </span>

              <input
                type="time"
                value={form.end_time}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    end_time: event.target.value,
                  }))
                }
                required
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-500"
              />
            </label>
          </div>

          <div className="rounded-xl border bg-slate-50 px-4 py-3 text-sm text-slate-600">
            Duration:{" "}
            <strong className="text-slate-900">
              {formatDuration(
                durationMinutes(form.start_time, form.end_time)
              )}
            </strong>

            {minutesFromTime(form.end_time) <
              minutesFromTime(form.start_time) && (
              <span className="ml-2 text-indigo-700">
                Overnight visit
              </span>
            )}
          </div>

          {conflicts.length > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <div className="flex gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />

                <div>
                  <p className="font-semibold text-red-900">
                    Worker scheduling conflict
                  </p>

                  <p className="mt-1 text-sm text-red-700">
                    This worker already has {conflicts.length} overlapping
                    visit{conflicts.length === 1 ? "" : "s"}.
                  </p>

                  <div className="mt-2 space-y-1 text-sm text-red-800">
                    {conflicts.map((visit) => (
                      <p key={visit.id}>
                        {visit.start_time}–{visit.end_time}:{" "}
                        {visit.client_name || "Participant"}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-1.5">
              <span className="text-sm font-semibold text-slate-700">
                Service type
              </span>

              <select
                value={form.service_type}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    service_type: event.target.value,
                  }))
                }
                required
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-500"
              >
                <option value="">Select service</option>

                {SERVICE_TYPES.map((service) => (
                  <option key={service} value={service}>
                    {service}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-1.5">
              <span className="text-sm font-semibold text-slate-700">
                Status
              </span>

              <select
                value={form.status}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    status: event.target.value,
                  }))
                }
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-500"
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {form.id && form.recurring_series_id && (
            <section className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
              <div className="flex items-start gap-3">
                <Repeat2 className="mt-0.5 h-5 w-5 shrink-0 text-indigo-700" />
                <div>
                  <h3 className="font-bold text-indigo-950">
                    Editing recurring roster
                  </h3>
                  <p className="mt-1 text-sm text-indigo-800">
                    {form.edit_scope === "this" &&
                      "Changes apply to this occurrence only."}
                    {form.edit_scope === "future" &&
                      "Changes apply to this and all future occurrences."}
                    {form.edit_scope === "all" &&
                      "Changes apply to the entire recurring series. Protected historical visits remain unchanged."}
                  </p>
                </div>
              </div>
            </section>
          )}

          {!form.id && (
            <section className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
              <div className="flex items-start gap-3">
                <Repeat2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />

                <div className="min-w-0 flex-1">
                  <h3 className="font-bold text-emerald-950">
                    Scheduling pattern
                  </h3>

                  <p className="mt-1 text-sm text-emerald-800">
                    Create one visit or generate dated visits from a weekly or fortnightly series.
                  </p>
                </div>
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <label className="space-y-1.5">
                  <span className="text-sm font-semibold text-slate-700">
                    Repeats
                  </span>

                  <select
                    value={form.recurrence_type}
                    onChange={(event) => {
                      const recurrenceType = event.target.value;
                      const selectedWeekday =
                        weekdayIndexFromDate(form.date);

                      setForm((current) => ({
                        ...current,
                        recurrence_type: recurrenceType,
                        recurrence_weekdays:
                          recurrenceType === "none"
                            ? []
                            : current.recurrence_weekdays.length
                              ? current.recurrence_weekdays
                              : selectedWeekday === null
                                ? []
                                : [selectedWeekday],
                      }));
                    }}
                    className="w-full rounded-xl border border-emerald-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                  >
                    <option value="none">Does not repeat</option>
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                  </select>
                </label>

                {form.recurrence_type !== "none" && (
                  <label className="space-y-1.5">
                    <span className="text-sm font-semibold text-slate-700">
                      Generate ahead
                    </span>

                    <select
                      value={form.generate_weeks}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          generate_weeks:
                            Number(event.target.value) || 12,
                        }))
                      }
                      className="w-full rounded-xl border border-emerald-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    >
                      {[8, 9, 10, 11, 12].map((weeks) => (
                        <option key={weeks} value={weeks}>
                          {weeks} weeks
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>

              {form.recurrence_type !== "none" && (
                <>
                  <div className="mt-4">
                    <span className="text-sm font-semibold text-slate-700">
                      Repeat on
                    </span>

                    <div className="mt-2 grid grid-cols-4 gap-2 sm:grid-cols-7">
                      {WEEKDAY_OPTIONS.map((day) => {
                        const selected =
                          form.recurrence_weekdays.includes(
                            day.value
                          );

                        return (
                          <button
                            key={day.value}
                            type="button"
                            onClick={() =>
                              setForm((current) => ({
                                ...current,
                                recurrence_weekdays:
                                  selected
                                    ? current.recurrence_weekdays.filter(
                                        (value) => value !== day.value
                                      )
                                    : [
                                        ...current.recurrence_weekdays,
                                        day.value,
                                      ].sort((a, b) => a - b),
                              }))
                            }
                            className={`rounded-lg border px-2 py-2 text-xs font-bold ${
                              selected
                                ? "border-emerald-600 bg-emerald-600 text-white"
                                : "border-emerald-200 bg-white text-slate-600 hover:bg-emerald-50"
                            }`}
                          >
                            {day.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <label className="space-y-1.5">
                      <span className="text-sm font-semibold text-slate-700">
                        Series ends
                      </span>

                      <select
                        value={form.recurrence_end_mode}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            recurrence_end_mode:
                              event.target.value,
                            recurrence_end_date:
                              event.target.value === "never"
                                ? ""
                                : current.recurrence_end_date,
                          }))
                        }
                        className="w-full rounded-xl border border-emerald-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                      >
                        <option value="never">No end date</option>
                        <option value="date">On selected date</option>
                      </select>
                    </label>

                    {form.recurrence_end_mode === "date" && (
                      <label className="space-y-1.5">
                        <span className="text-sm font-semibold text-slate-700">
                          End date
                        </span>

                        <input
                          type="date"
                          min={form.date}
                          value={form.recurrence_end_date}
                          onChange={(event) =>
                            setForm((current) => ({
                              ...current,
                              recurrence_end_date:
                                event.target.value,
                            }))
                          }
                          required
                          className="w-full rounded-xl border border-emerald-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                        />
                      </label>
                    )}
                  </div>

                  <div className="mt-4 rounded-xl border border-emerald-200 bg-white px-4 py-3 text-sm text-emerald-900">
                    <strong>
                      {recurrenceLabel(form.recurrence_type)}
                    </strong>{" "}
                    series beginning {form.date || "on the selected date"}.
                    Individual dated visits will be generated for clock-on,
                    mileage, notes, timesheets, payroll and Xero.
                  </div>
                </>
              )}
            </section>
          )}

          <label className="block space-y-1.5">
            <span className="text-sm font-semibold text-slate-700">
              Visit address
            </span>

            <input
              value={form.address}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  address: event.target.value,
                }))
              }
              placeholder={
                selectedParticipant
                  ? "Participant address"
                  : "Enter service address"
              }
              required
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-500"
            />
          </label>

          <div className="grid gap-4 md:grid-cols-[1fr_200px]">
            <label className="space-y-1.5">
              <span className="text-sm font-semibold text-slate-700">
                Visit notes
              </span>

              <textarea
                rows={3}
                value={form.notes}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    notes: event.target.value,
                  }))
                }
                placeholder="Scheduling or service instructions"
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-500"
              />
            </label>

            <label className="space-y-1.5">
              <span className="text-sm font-semibold text-slate-700">
                Geofence radius
              </span>

              <div className="relative">
                <input
                  type="number"
                  min="25"
                  max="1000"
                  step="25"
                  value={form.geofence_radius_m}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      geofence_radius_m:
                        Number(event.target.value) || 100,
                    }))
                  }
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 pr-10 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 disabled:bg-slate-100 disabled:text-slate-500"
                />

                <span className="absolute right-3 top-2.5 text-sm text-slate-400">
                  m
                </span>
              </div>
            </label>
          </div>

          <div className="flex flex-col-reverse gap-3 border-t pt-5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving || conflicts.length > 0}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}

              {form.id
                ? "Save changes"
                : form.recurrence_type === "none"
                  ? "Create visit"
                  : "Create recurring series"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function RosterBoard() {
  const [weekStart, setWeekStart] = useState(() =>
    startOfWeek(new Date())
  );

  const [visits, setVisits] = useState([]);
  const [staff, setStaff] = useState([]);
  const [participants, setParticipants] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [staffFilter, setStaffFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [openSeriesMenuId, setOpenSeriesMenuId] =
    useState(null);
  const [draggingVisitId, setDraggingVisitId] =
    useState(null);
  const [dragOverDate, setDragOverDate] =
    useState(null);
  const [dragOverWorkerId, setDragOverWorkerId] =
    useState(null);
  const [resizeState, setResizeState] = useState(null);
  const [compactCards, setCompactCards] = useState(false);
  const [plannerView, setPlannerView] = useState("staff");
  const [smartScheduling, setSmartScheduling] = useState(false);
  const [smartSchedulingResult, setSmartSchedulingResult] =
    useState(null);

  const weekDays = useMemo(
    () =>
      Array.from({ length: 7 }, (_, index) =>
        addDays(weekStart, index)
      ),
    [weekStart]
  );

  const weekStartString = localDateString(weekStart);
  const weekEndString = localDateString(addDays(weekStart, 6));

  const loadRoster = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [
        visitsResponse,
        staffResponse,
        participantsResponse,
      ] = await Promise.all([
        api.get("/roster/week", {
          params: {
            start_date: weekStartString,
            end_date: weekEndString,
          },
        }),
        api.get("/staff", {
          params: {
            include_inactive: false,
          },
        }),
        api.get("/clients"),
      ]);

      setVisits(
        Array.isArray(visitsResponse?.data?.visits)
          ? visitsResponse.data.visits
          : normalizeListResponse(visitsResponse)
      );
      setStaff(normalizeListResponse(staffResponse));
      setParticipants(normalizeListResponse(participantsResponse));
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to load the roster."
      );
    } finally {
      setLoading(false);
    }
  }, [weekEndString, weekStartString]);

  useEffect(() => {
    loadRoster();
  }, [loadRoster]);

  const staffLookup = useMemo(
    () =>
      Object.fromEntries(
        staff.map((worker) => [
          worker.id,
          getPersonName(worker, "Unknown worker"),
        ])
      ),
    [staff]
  );

  const participantLookup = useMemo(
    () =>
      Object.fromEntries(
        participants.map((participant) => [
          participant.id,
          getPersonName(participant, "Participant"),
        ])
      ),
    [participants]
  );

  const weekVisits = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return visits.filter((visit) => {
      if (
        visit.date < weekStartString ||
        visit.date > weekEndString
      ) {
        return false;
      }

      if (
        staffFilter &&
        visit.staff_id !== staffFilter
      ) {
        return false;
      }

      if (
        statusFilter &&
        visit.status !== statusFilter
      ) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const haystack = [
        visit.client_name,
        participantLookup[visit.client_id],
        visit.staff_name,
        staffLookup[visit.staff_id],
        visit.service_type,
        visit.address,
        visit.notes,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [
    participantLookup,
    search,
    staffFilter,
    staffLookup,
    statusFilter,
    visits,
    weekEndString,
    weekStartString,
  ]);

  const visitsByDate = useMemo(() => {
    const result = {};

    weekDays.forEach((day) => {
      result[localDateString(day)] = [];
    });

    weekVisits.forEach((visit) => {
      if (!result[visit.date]) {
        result[visit.date] = [];
      }

      result[visit.date].push(visit);
    });

    Object.values(result).forEach((items) => {
      items.sort((first, second) =>
        String(first.start_time || "").localeCompare(
          String(second.start_time || "")
        )
      );
    });

    return result;
  }, [weekDays, weekVisits]);

  const conflictsByVisitId = useMemo(() => {
    const conflictIds = new Set();

    weekVisits.forEach((visit, firstIndex) => {
      if (
        !visit.staff_id ||
        visit.status === "cancelled"
      ) {
        return;
      }

      weekVisits
        .slice(firstIndex + 1)
        .forEach((other) => {
          if (
            other.status === "cancelled" ||
            other.staff_id !== visit.staff_id ||
            other.date !== visit.date
          ) {
            return;
          }

          if (visitsOverlap(visit, other)) {
            conflictIds.add(visit.id);
            conflictIds.add(other.id);
          }
        });
    });

    return conflictIds;
  }, [weekVisits]);

  const summary = useMemo(() => {
    const activeVisits = weekVisits.filter(
      (visit) => visit.status !== "cancelled"
    );

    return {
      total: weekVisits.length,
      drafts: weekVisits.filter(
        (visit) =>
          visit.status === "draft" ||
          visit.publication_status === "draft"
      ).length,
      upcoming: weekVisits.filter(
        (visit) => visit.status === "upcoming"
      ).length,
      inProgress: weekVisits.filter(
        (visit) => visit.status === "in_progress"
      ).length,
      completed: weekVisits.filter(
        (visit) => visit.status === "completed"
      ).length,
      cancelled: weekVisits.filter(
        (visit) => visit.status === "cancelled"
      ).length,
      hours: activeVisits.reduce(
        (total, visit) =>
          total +
          durationMinutes(
            visit.start_time,
            visit.end_time
          ),
        0
      ),
      workers: new Set(
        activeVisits
          .map((visit) => visit.staff_id)
          .filter(Boolean)
      ).size,
      conflicts: conflictsByVisitId.size,
    };
  }, [conflictsByVisitId, weekVisits]);

  const smartSchedulingInsights = useMemo(() => {
    const minimumTravelBufferMinutes = 30;
    const weeklyLimitMinutes = 38 * 60;

    const activeVisits = weekVisits.filter(
      (visit) => visit.status !== "cancelled"
    );

    const unfilledVisits = activeVisits.filter(
      (visit) => !visit.staff_id
    );

    const weeklyMinutesByStaff = {};
    const visitsByStaffDate = {};

    activeVisits.forEach((visit) => {
      if (!visit.staff_id) {
        return;
      }

      weeklyMinutesByStaff[visit.staff_id] =
        (weeklyMinutesByStaff[visit.staff_id] || 0)
        + durationMinutes(
          visit.start_time,
          visit.end_time
        );

      const key = `${visit.staff_id}:${visit.date}`;

      if (!visitsByStaffDate[key]) {
        visitsByStaffDate[key] = [];
      }

      visitsByStaffDate[key].push(visit);
    });

    Object.values(visitsByStaffDate).forEach((items) => {
      items.sort((first, second) =>
        String(first.start_time || "").localeCompare(
          String(second.start_time || "")
        )
      );
    });

    const doubleBookedIds = new Set(
      conflictsByVisitId
    );

    const travelWarnings = [];

    Object.entries(visitsByStaffDate).forEach(
      ([key, items]) => {
        for (let index = 0; index < items.length - 1; index += 1) {
          const current = items[index];
          const next = items[index + 1];

          const currentEnd = minutesFromTime(
            current.end_time
          );
          const nextStart = minutesFromTime(
            next.start_time
          );

          if (
            currentEnd === null ||
            nextStart === null
          ) {
            continue;
          }

          const adjustedNextStart =
            nextStart < currentEnd
              ? nextStart + 24 * 60
              : nextStart;

          const gapMinutes =
            adjustedNextStart - currentEnd;

          const sameAddress =
            current.address &&
            next.address &&
            String(current.address)
              .trim()
              .toLowerCase() ===
              String(next.address)
                .trim()
                .toLowerCase();

          const requiredMinutes =
            sameAddress ? 0 : minimumTravelBufferMinutes;

          if (
            gapMinutes >= 0 &&
            gapMinutes < requiredMinutes
          ) {
            const [staffId, date] = key.split(":");

            travelWarnings.push({
              id: `${current.id}:${next.id}`,
              staff_id: staffId,
              date,
              first_visit_id: current.id,
              second_visit_id: next.id,
              gap_minutes: gapMinutes,
              required_minutes: requiredMinutes,
              first_end_time: current.end_time,
              second_start_time: next.start_time,
            });
          }
        }
      }
    );

    const overtimeStaff = staff
      .map((worker) => {
        const minutes =
          weeklyMinutesByStaff[worker.id] || 0;

        return {
          staff_id: worker.id,
          staff_name: getPersonName(
            worker,
            "Support worker"
          ),
          minutes,
          overtime_minutes: Math.max(
            0,
            minutes - weeklyLimitMinutes
          ),
        };
      })
      .filter((item) => item.overtime_minutes > 0)
      .sort(
        (first, second) =>
          second.overtime_minutes -
          first.overtime_minutes
      );

    return {
      minimum_travel_buffer_minutes:
        minimumTravelBufferMinutes,
      weekly_limit_minutes: weeklyLimitMinutes,
      unfilled_visits: unfilledVisits,
      unfilled_count: unfilledVisits.length,
      double_booked_count: doubleBookedIds.size,
      travel_warnings: travelWarnings,
      travel_warning_count: travelWarnings.length,
      overtime_staff: overtimeStaff,
      overtime_count: overtimeStaff.length,
      weekly_minutes_by_staff: weeklyMinutesByStaff,
      visits_by_staff_date: visitsByStaffDate,
    };
  }, [
    conflictsByVisitId,
    staff,
    weekVisits,
  ]);

  const findSmartAssignment = useCallback(
    (visit) => {
      if (!visit) {
        return null;
      }

      const visitMinutes = durationMinutes(
        visit.start_time,
        visit.end_time
      );

      const visitStart = minutesFromTime(
        visit.start_time
      );
      const visitEndRaw = minutesFromTime(
        visit.end_time
      );

      if (
        visitStart === null ||
        visitEndRaw === null
      ) {
        return null;
      }

      const visitEnd =
        visitEndRaw > visitStart
          ? visitEndRaw
          : visitEndRaw + 24 * 60;

      const candidates = staff
        .map((worker) => {
          const workerVisits =
            smartSchedulingInsights
              .visits_by_staff_date[
                `${worker.id}:${visit.date}`
              ] || [];

          let overlap = false;
          let minimumGap = Number.POSITIVE_INFINITY;

          workerVisits.forEach((existing) => {
            const existingStart = minutesFromTime(
              existing.start_time
            );
            const existingEndRaw = minutesFromTime(
              existing.end_time
            );

            if (
              existingStart === null ||
              existingEndRaw === null
            ) {
              return;
            }

            const existingEnd =
              existingEndRaw > existingStart
                ? existingEndRaw
                : existingEndRaw + 24 * 60;

            if (
              visitStart < existingEnd &&
              existingStart < visitEnd
            ) {
              overlap = true;
            }

            const beforeGap =
              visitStart - existingEnd;
            const afterGap =
              existingStart - visitEnd;

            [beforeGap, afterGap]
              .filter((gap) => gap >= 0)
              .forEach((gap) => {
                minimumGap = Math.min(
                  minimumGap,
                  gap
                );
              });
          });

          const currentWeeklyMinutes =
            smartSchedulingInsights
              .weekly_minutes_by_staff[
                worker.id
              ] || 0;

          const projectedWeeklyMinutes =
            currentWeeklyMinutes + visitMinutes;

          const sameDayHasVisits =
            workerVisits.length > 0;

          const travelBufferSatisfied =
            !sameDayHasVisits ||
            minimumGap === Number.POSITIVE_INFINITY ||
            minimumGap >=
              smartSchedulingInsights
                .minimum_travel_buffer_minutes;

          const projectedOvertimeMinutes =
            Math.max(
              0,
              projectedWeeklyMinutes -
                smartSchedulingInsights
                  .weekly_limit_minutes
            );

          const score =
            (overlap ? 100000 : 0)
            + (
              travelBufferSatisfied
                ? 0
                : 10000
            )
            + projectedOvertimeMinutes * 10
            + projectedWeeklyMinutes
            + (
              sameDayHasVisits
                ? 100
                : 0
            );

          return {
            worker,
            score,
            overlap,
            travel_buffer_satisfied:
              travelBufferSatisfied,
            projected_weekly_minutes:
              projectedWeeklyMinutes,
            projected_overtime_minutes:
              projectedOvertimeMinutes,
          };
        })
        .filter(
          (candidate) =>
            !candidate.overlap &&
            candidate.travel_buffer_satisfied
        )
        .sort(
          (first, second) =>
            first.score - second.score
        );

      return candidates[0] || null;
    },
    [
      smartSchedulingInsights,
      staff,
    ]
  );

  const runOneClickAutoAssign = async () => {
    const unfilled =
      smartSchedulingInsights.unfilled_visits;

    if (!unfilled.length) {
      setSmartSchedulingResult({
        assigned: 0,
        skipped: 0,
        message:
          "There are no unfilled visits in this roster week.",
      });
      return;
    }

    const plan = [];
    const skipped = [];

    unfilled.forEach((visit) => {
      const candidate =
        findSmartAssignment(visit);

      if (!candidate) {
        skipped.push({
          visit,
          reason:
            "No worker met the overlap, travel-buffer and overtime rules.",
        });
        return;
      }

      plan.push({
        visit,
        candidate,
      });
    });

    const previewLines = plan.map(
      ({ visit, candidate }) => {
        const participant =
          visit.client_name ||
          participantLookup[visit.client_id] ||
          "Participant";

        return (
          `${participant} — ${visit.date} `
          + `${String(
            visit.start_time || ""
          ).slice(0, 5)}–${String(
            visit.end_time || ""
          ).slice(0, 5)}`
          + ` → ${getPersonName(
            candidate.worker,
            "Support worker"
          )}`
        );
      }
    );

    const confirmed = window.confirm(
      `Project Atlas Smart Scheduling\n\n`
      + `Ready to assign: ${plan.length}\n`
      + `Unable to assign: ${skipped.length}\n\n`
      + (
        previewLines.length
          ? previewLines
              .slice(0, 12)
              .join("\n")
          : "No safe assignments were found."
      )
      + (
        previewLines.length > 12
          ? `\n...and ${
              previewLines.length - 12
            } more`
          : ""
      )
      + "\n\nAssignments will be returned to Draft for review."
    );

    if (!confirmed || !plan.length) {
      return;
    }

    setSmartScheduling(true);
    setError("");
    setSuccess("");

    let assigned = 0;
    const failures = [];

    try {
      for (const item of plan) {
        const visit = item.visit;
        const worker = item.candidate.worker;

        try {
          await api.put(`/visits/${visit.id}`, {
            client_id: visit.client_id,
            staff_id: worker.id,
            date: visit.date,
            start_time: String(
              visit.start_time || "09:00"
            ).slice(0, 5),
            end_time: String(
              visit.end_time || "10:00"
            ).slice(0, 5),
            service_type:
              visit.service_type || "",
            address: visit.address || "",
            notes: visit.notes || null,
            geofence_radius_m:
              Number(
                visit.geofence_radius_m
              ) || 100,
            status:
              visit.status || "upcoming",
          });

          await api.post(
            `/roster/visits/${visit.id}/draft`
          );

          assigned += 1;
        } catch (assignmentError) {
          failures.push({
            visit_id: visit.id,
            error:
              assignmentError?.response?.data
                ?.detail ||
              assignmentError?.message ||
              "Assignment failed",
          });
        }
      }

      setSmartSchedulingResult({
        assigned,
        skipped:
          skipped.length + failures.length,
        message:
          `${assigned} visit(s) auto-assigned. `
          + `${skipped.length + failures.length} `
          + `visit(s) require manual review.`,
      });

      setSuccess(
        `${assigned} unfilled visit(s) auto-assigned and returned to Draft.`
      );

      await loadRoster();
    } finally {
      setSmartScheduling(false);
    }
  };

  const openCreate = (
    date = weekStartString,
    staffId = ""
  ) => {
    setError("");
    setSuccess("");

    const weekday = weekdayIndexFromDate(date);

    setForm({
      ...EMPTY_FORM,
      date,
      staff_id: staffId,
      recurrence_weekdays:
        weekday === null ? [] : [weekday],
    });

    setModalOpen(true);
  };

  const openEdit = (visit) => {
    setError("");
    setSuccess("");

    setForm({
      id: visit.id,
      client_id: visit.client_id || "",
      staff_id: visit.staff_id || "",
      date: visit.date || weekStartString,
      start_time: String(visit.start_time || "09:00").slice(0, 5),
      end_time: String(visit.end_time || "10:00").slice(0, 5),
      service_type: visit.service_type || "",
      address: visit.address || "",
      notes: visit.notes || "",
      geofence_radius_m:
        Number(visit.geofence_radius_m) || 100,
      status: visit.status || "upcoming",
      recurring_series_id: visit.recurring_series_id || null,
      recurring_occurrence_date:
        visit.recurring_occurrence_date ||
        visit.date ||
        null,
      edit_scope: "this",
      recurrence_type: "none",
      recurrence_weekdays: [],
      recurrence_end_mode: "never",
      recurrence_end_date: "",
      generate_weeks: 12,
    });

    setModalOpen(true);
  };

  const openRecurringEdit = (visit) => {
    const answer = window.prompt(
      "Edit recurring visit:\n\n" +
        "1 = This occurrence only\n" +
        "2 = This and future occurrences\n" +
        "3 = Entire recurring series\n\n" +
        "Enter 1, 2 or 3:",
      "1"
    );

    if (answer === null) {
      return;
    }

    const scopeMap = {
      "1": "this",
      "2": "future",
      "3": "all",
    };

    const scope = scopeMap[String(answer).trim()];

    if (!scope) {
      setError("Choose 1, 2 or 3 for the recurring edit scope.");
      return;
    }

    setError("");
    setSuccess("");

    setForm({
      id: visit.id,
      client_id: visit.client_id || "",
      staff_id: visit.staff_id || "",
      date: visit.date || weekStartString,
      start_time: String(visit.start_time || "09:00").slice(0, 5),
      end_time: String(visit.end_time || "10:00").slice(0, 5),
      service_type: visit.service_type || "",
      address: visit.address || "",
      notes: visit.notes || "",
      geofence_radius_m:
        Number(visit.geofence_radius_m) || 100,
      status: visit.status || "upcoming",
      recurring_series_id: visit.recurring_series_id,
      recurring_occurrence_date:
        visit.recurring_occurrence_date ||
        visit.date,
      edit_scope: scope,
      recurrence_type: "none",
      recurrence_weekdays: [],
      recurrence_end_mode: "never",
      recurrence_end_date: "",
      generate_weeks: 12,
    });

    setOpenSeriesMenuId(null);
    setModalOpen(true);
  };

  const duplicateVisit = (visit) => {
    setError("");
    setSuccess("");

    setForm({
      id: null,
      client_id: visit.client_id || "",
      staff_id: visit.staff_id || "",
      date: visit.date || weekStartString,
      start_time: String(visit.start_time || "09:00").slice(0, 5),
      end_time: String(visit.end_time || "10:00").slice(0, 5),
      service_type: visit.service_type || "",
      address: visit.address || "",
      notes: visit.notes || "",
      geofence_radius_m:
        Number(visit.geofence_radius_m) || 100,
      status: "draft",
      recurring_series_id: null,
      recurring_occurrence_date: null,
      edit_scope: "this",
      recurrence_type: "none",
      recurrence_weekdays: [],
      recurrence_end_mode: "never",
      recurrence_end_date: "",
      generate_weeks: 12,
    });

    setModalOpen(true);
  };

  const submitVisit = async (event) => {
    event.preventDefault();

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        client_id: form.client_id,
        staff_id: form.staff_id,
        date: form.date,
        start_time: form.start_time,
        end_time: form.end_time,
        service_type: form.service_type,
        address: form.address,
        notes: form.notes || null,
        geofence_radius_m:
          Number(form.geofence_radius_m) || 100,
        status: form.status || "upcoming",
      };

      if (form.id && form.recurring_series_id) {
        await api.patch(
          `/roster/series/${form.recurring_series_id}/apply-change`,
          {
            scope: form.edit_scope || "this",
            occurrence_date:
              form.recurring_occurrence_date ||
              form.date,
            visit_id:
              form.edit_scope === "this"
                ? form.id
                : null,
            client_id: form.client_id,
            staff_id: form.staff_id,
            service_type: form.service_type,
            start_time: form.start_time,
            end_time: form.end_time,
            address: form.address,
            notes: form.notes || null,
            geofence_radius_m:
              Number(form.geofence_radius_m) || 100,
            reason: "Roster Board recurring edit",
            generate_weeks: 12,
          }
        );

        setSuccess(
          form.edit_scope === "this"
            ? "This recurring occurrence was updated."
            : form.edit_scope === "future"
              ? "This and future recurring occurrences were updated."
              : "The recurring series was updated. Protected historical visits were preserved."
        );
      } else if (form.id) {
        await api.put(`/visits/${form.id}`, payload);
        await api.post(`/roster/visits/${form.id}/draft`);
        setSuccess(
          "Rostered visit updated and returned to Draft. Publish the day or week when ready."
        );
      } else if (form.recurrence_type !== "none") {
        if (!form.recurrence_weekdays.length) {
          throw new Error(
            "Select at least one weekday for the recurring series."
          );
        }

        if (
          form.recurrence_end_mode === "date" &&
          !form.recurrence_end_date
        ) {
          throw new Error(
            "Select an end date for the recurring series."
          );
        }

        const seriesPayload = {
          client_id: form.client_id,
          staff_id: form.staff_id,
          service_type: form.service_type,
          default_start_time: form.start_time,
          default_end_time: form.end_time,
          repeat_type: form.recurrence_type,
          weekdays: form.recurrence_weekdays,
          start_date: form.date,
          end_date:
            form.recurrence_end_mode === "date"
              ? form.recurrence_end_date
              : null,
          generate_weeks:
            Number(form.generate_weeks) || 12,
          publication_status: "draft",
          notes: form.notes || null,
          address: form.address,
          geofence_radius_m:
            Number(form.geofence_radius_m) || 100,
        };

        const response = await api.post(
          "/roster/series",
          seriesPayload,
          {
            params: {
              generate_now: true,
            },
          }
        );

        const createdCount =
          response?.data?.generation?.created ?? 0;

        setSuccess(
          `${recurrenceLabel(
            form.recurrence_type
          )} roster series created with ${createdCount} draft visit(s). Publish the relevant day or week when ready.`
        );
      } else {
        const createdResponse = await api.post("/visits", payload);
        const createdId = createdResponse?.data?.id;

        if (!createdId) {
          throw new Error("Visit was created but no visit ID was returned");
        }

        await api.post(`/roster/visits/${createdId}/draft`);

        setSuccess(
          "Rostered visit created as Draft. Publish the day or week when ready."
        );
      }

      setModalOpen(false);
      await loadRoster();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to save the rostered visit."
      );
    } finally {
      setSaving(false);
    }
  };

  const publishDay = async (date) => {
    const dayVisits = visits.filter(
      (visit) =>
        visit.date === date &&
        visit.status !== "cancelled"
    );

    if (!dayVisits.length) {
      setError("There are no rostered visits to publish for this day.");
      return;
    }

    const confirmed = window.confirm(
      `Publish ${dayVisits.length} visit(s) for ${date} to the assigned support workers?`
    );

    if (!confirmed) {
      return;
    }

    setPublishing(true);
    setError("");
    setSuccess("");

    try {
      const response = await api.post("/roster/publish-day", null, {
        params: { date },
      });

      const count =
        response?.data?.published_count ?? dayVisits.length;

      setSuccess(
        `${count} visit(s) published for ${date}. Assigned workers can now see them.`
      );

      await loadRoster();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to publish this roster day."
      );
    } finally {
      setPublishing(false);
    }
  };

  const publishWeek = async () => {
    const publishable = visits.filter(
      (visit) =>
        visit.date >= weekStartString &&
        visit.date <= weekEndString &&
        visit.status !== "cancelled"
    );

    if (!publishable.length) {
      setError("There are no rostered visits to publish this week.");
      return;
    }

    const draftCount = publishable.filter(
      (visit) =>
        visit.publication_status === "draft" ||
        visit.status === "draft"
    ).length;

    const smartWarnings = [
      smartSchedulingInsights.unfilled_count
        ? `${smartSchedulingInsights.unfilled_count} unfilled visit(s)`
        : null,
      smartSchedulingInsights.double_booked_count
        ? `${smartSchedulingInsights.double_booked_count} double-booked visit(s)`
        : null,
      smartSchedulingInsights.travel_warning_count
        ? `${smartSchedulingInsights.travel_warning_count} travel-buffer warning(s)`
        : null,
      smartSchedulingInsights.overtime_count
        ? `${smartSchedulingInsights.overtime_count} worker(s) above 38 hours`
        : null,
    ].filter(Boolean);

    const confirmed = window.confirm(
      `Publish this roster week to staff?\n\n` +
      `${publishable.length} visit(s)\n` +
      `${draftCount} draft visit(s)\n` +
      (
        smartWarnings.length
          ? `\nSMART SCHEDULING WARNINGS:\n- ${
              smartWarnings.join("\n- ")
            }\n`
          : ""
      ) +
      `\nAssigned workers will be able to view the published visits.`
    );

    if (!confirmed) {
      return;
    }

    setPublishing(true);
    setError("");
    setSuccess("");

    try {
      const response = await api.post("/roster/publish-week", null, {
        params: {
          week_start: weekStartString,
        },
      });

      const count =
        response?.data?.published_count ?? publishable.length;

      const notifications =
        response?.data?.notification_count ?? 0;

      setSuccess(
        `${count} visit(s) published. ${notifications} worker notification(s) created.`
      );

      await loadRoster();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to publish the roster week."
      );
    } finally {
      setPublishing(false);
    }
  };

  const skipRecurringOccurrence = async (visit) => {
    const confirmed = window.confirm(
      `Skip the recurring occurrence on ${visit.date}?\n\nThe rest of the series will remain active.`
    );

    if (!confirmed) {
      return;
    }

    setError("");
    setSuccess("");
    setOpenSeriesMenuId(null);

    try {
      await api.post(
        `/roster/series/${visit.recurring_series_id}/skip`,
        {
          occurrence_date:
            visit.recurring_occurrence_date ||
            visit.date,
          reason: "Skipped from Roster Board",
        }
      );

      setSuccess("Recurring occurrence skipped.");
      await loadRoster();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to skip the recurring occurrence."
      );
    }
  };

  const changeRecurringTime = async (visit) => {
    const startTime = window.prompt(
      "New start time (HH:MM):",
      String(visit.start_time || "09:00").slice(0, 5)
    );

    if (startTime === null) {
      return;
    }

    const endTime = window.prompt(
      "New end time (HH:MM):",
      String(visit.end_time || "10:00").slice(0, 5)
    );

    if (endTime === null) {
      return;
    }

    setError("");
    setSuccess("");
    setOpenSeriesMenuId(null);

    try {
      await api.post(
        `/roster/series/${visit.recurring_series_id}/change-time`,
        {
          occurrence_date:
            visit.recurring_occurrence_date ||
            visit.date,
          start_time: startTime,
          end_time: endTime,
          reason: "Changed from Roster Board",
        }
      );

      setSuccess("Recurring occurrence time changed.");
      await loadRoster();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to change the recurring occurrence time."
      );
    }
  };

  const replaceRecurringWorker = async (visit) => {
    const availableWorkers = staff
      .map(
        (worker, index) =>
          `${index + 1}. ${getPersonName(worker, "Worker")}`
      )
      .join("\n");

    const answer = window.prompt(
      `Choose replacement worker for ${visit.date}:\n\n${availableWorkers}\n\nEnter worker number:`
    );

    if (answer === null) {
      return;
    }

    const replacement = staff[Number(answer) - 1];

    if (!replacement) {
      setError("Select a valid worker number.");
      return;
    }

    setError("");
    setSuccess("");
    setOpenSeriesMenuId(null);

    try {
      await api.post(
        `/roster/series/${visit.recurring_series_id}/replace-worker`,
        {
          occurrence_date:
            visit.recurring_occurrence_date ||
            visit.date,
          replacement_staff_id: replacement.id,
          reason: "Replacement assigned from Roster Board",
        }
      );

      setSuccess(
        `${getPersonName(
          replacement,
          "Replacement worker"
        )} assigned to this occurrence.`
      );
      await loadRoster();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to replace the worker."
      );
    }
  };

  const toggleRecurringPause = async (visit) => {
    setError("");
    setSuccess("");
    setOpenSeriesMenuId(null);

    try {
      const seriesResponse = await api.get(
        `/roster/series/${visit.recurring_series_id}`
      );
      const series = seriesResponse?.data?.series || {};

      if (series.paused) {
        if (
          !window.confirm(
            "Resume this recurring series and regenerate future visits?"
          )
        ) {
          return;
        }

        await api.post(
          `/roster/series/${visit.recurring_series_id}/resume`,
          { generate_weeks: 12 }
        );

        setSuccess("Recurring series resumed.");
      } else {
        const pauseFrom = window.prompt(
          "Pause series from date (YYYY-MM-DD):",
          visit.date
        );

        if (pauseFrom === null) {
          return;
        }

        const resumeOn = window.prompt(
          "Optional resume date (YYYY-MM-DD). Leave blank for manual resume:",
          ""
        );

        await api.post(
          `/roster/series/${visit.recurring_series_id}/pause`,
          {
            pause_from: pauseFrom,
            resume_on: resumeOn || null,
            reason: "Paused from Roster Board",
          }
        );

        setSuccess(
          resumeOn
            ? `Recurring series paused until ${resumeOn}.`
            : "Recurring series paused until manually resumed."
        );
      }

      await loadRoster();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to pause or resume the recurring series."
      );
    }
  };

  const ceaseRecurringSeries = async (visit) => {
    const ceaseDate = window.prompt(
      "Cease recurring series after date (YYYY-MM-DD):",
      visit.date
    );

    if (ceaseDate === null) {
      return;
    }

    if (
      !window.confirm(
        `Cease this recurring series after ${ceaseDate}?\n\nCompleted and protected historical visits will remain.`
      )
    ) {
      return;
    }

    setError("");
    setSuccess("");
    setOpenSeriesMenuId(null);

    try {
      await api.post(
        `/roster/series/${visit.recurring_series_id}/cease`,
        null,
        { params: { cease_date: ceaseDate } }
      );

      setSuccess(
        `Recurring series ceased after ${ceaseDate}.`
      );
      await loadRoster();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to cease the recurring series."
      );
    }
  };

  const updateVisitWorker = async (
    visit,
    worker
  ) => {
    if (!worker || worker.id === visit.staff_id) {
      setDragOverWorkerId(null);
      return;
    }

    const workerName = getPersonName(
      worker,
      "Support worker"
    );

    if (
      !window.confirm(
        `Assign ${workerName} to the ${visit.date} visit?`
      )
    ) {
      setDragOverWorkerId(null);
      return;
    }

    setError("");
    setSuccess("");

    try {
      if (visit.recurring_series_id) {
        await api.post(
          `/roster/series/${visit.recurring_series_id}/replace-worker`,
          {
            occurrence_date:
              visit.recurring_occurrence_date ||
              visit.date,
            replacement_staff_id: worker.id,
            reason:
              "Reassigned by drag and drop on Roster Board",
          }
        );

        setSuccess(
          `${workerName} assigned to this recurring occurrence.`
        );
      } else {
        await api.put(`/visits/${visit.id}`, {
          client_id: visit.client_id,
          staff_id: worker.id,
          date: visit.date,
          start_time: String(
            visit.start_time || "09:00"
          ).slice(0, 5),
          end_time: String(
            visit.end_time || "10:00"
          ).slice(0, 5),
          service_type: visit.service_type || "",
          address: visit.address || "",
          notes: visit.notes || null,
          geofence_radius_m:
            Number(visit.geofence_radius_m) || 100,
          status: visit.status || "upcoming",
        });

        await api.post(
          `/roster/visits/${visit.id}/draft`
        );

        setSuccess(
          `${workerName} assigned. The visit was returned to Draft.`
        );
      }

      await loadRoster();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to reassign the worker."
      );
    } finally {
      setDraggingVisitId(null);
      setDragOverWorkerId(null);
    }
  };

  const updateVisitEndTime = useCallback(async (
    visit,
    endTime
  ) => {
    if (!endTime || endTime === visit.end_time) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      if (visit.recurring_series_id) {
        await api.post(
          `/roster/series/${visit.recurring_series_id}/change-time`,
          {
            occurrence_date:
              visit.recurring_occurrence_date ||
              visit.date,
            start_time: String(
              visit.start_time || "09:00"
            ).slice(0, 5),
            end_time: endTime,
            reason:
              "Time resized from Roster Board",
          }
        );

        setSuccess(
          `Recurring occurrence resized to end at ${endTime}.`
        );
      } else {
        await api.put(`/visits/${visit.id}`, {
          client_id: visit.client_id,
          staff_id: visit.staff_id,
          date: visit.date,
          start_time: String(
            visit.start_time || "09:00"
          ).slice(0, 5),
          end_time: endTime,
          service_type: visit.service_type || "",
          address: visit.address || "",
          notes: visit.notes || null,
          geofence_radius_m:
            Number(visit.geofence_radius_m) || 100,
          status: visit.status || "upcoming",
        });

        await api.post(
          `/roster/visits/${visit.id}/draft`
        );

        setSuccess(
          `Visit resized to end at ${endTime} and returned to Draft.`
        );
      }

      await loadRoster();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to resize the visit time."
      );
    }
  }, [loadRoster]);

  const handleResizePointerDown = (
    event,
    visit
  ) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      [
        "completed",
        "in_progress",
        "clocked_on",
        "clocked_off",
      ].includes(visit.status)
    ) {
      setError(
        "Completed or active visits cannot be resized."
      );
      return;
    }

    const startEndMinutes = minutesFromTime(
      visit.end_time
    );

    if (startEndMinutes === null) {
      return;
    }

    setResizeState({
      visit,
      startY: event.clientY,
      initialEndMinutes: startEndMinutes,
      previewEndTime: String(
        visit.end_time
      ).slice(0, 5),
    });
  };

  useEffect(() => {
    if (!resizeState) {
      return undefined;
    }

    const handlePointerMove = (event) => {
      const deltaY =
        event.clientY - resizeState.startY;
      const slots = Math.round(deltaY / 12);
      const nextEndMinutes =
        resizeState.initialEndMinutes +
        slots * 15;

      setResizeState((current) =>
        current
          ? {
              ...current,
              previewEndTime:
                timeFromMinutes(nextEndMinutes),
            }
          : current
      );
    };

    const handlePointerUp = async () => {
      const current = resizeState;
      setResizeState(null);

      if (current) {
        await updateVisitEndTime(
          current.visit,
          current.previewEndTime
        );
      }
    };

    window.addEventListener(
      "pointermove",
      handlePointerMove
    );
    window.addEventListener(
      "pointerup",
      handlePointerUp,
      { once: true }
    );

    return () => {
      window.removeEventListener(
        "pointermove",
        handlePointerMove
      );
      window.removeEventListener(
        "pointerup",
        handlePointerUp
      );
    };
  }, [resizeState, updateVisitEndTime]);

  const handleWorkerDragOver = (
    event,
    workerId
  ) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverWorkerId(workerId);
  };

  const handleWorkerDrop = async (
    event,
    worker
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const visitId =
      event.dataTransfer.getData(
        "application/x-cruzzn-visit-id"
      ) ||
      event.dataTransfer.getData("text/plain");

    const visit = visits.find(
      (item) => item.id === visitId
    );

    try {
      event.dataTransfer.clearData();
    } catch (clearError) {
      // Browser may lock the drag store after reading.
    }

    resetDragState();

    if (!visit) {
      setError("The dragged visit could not be found.");
      return;
    }

    await updateVisitWorker(visit, worker);
  };

  const moveVisitToDate = async (visit, targetDate) => {
    if (!targetDate || targetDate === visit.date) {
      setDraggingVisitId(null);
      setDragOverDate(null);
      return;
    }

    const participant =
      visit.client_name ||
      participantLookup[visit.client_id] ||
      "Participant";

    const confirmed = window.confirm(
      `Move ${participant} from ${visit.date} to ${targetDate}?\n\nThe visit time will remain ${String(
        visit.start_time || ""
      ).slice(0, 5)}–${String(
        visit.end_time || ""
      ).slice(0, 5)}.`
    );

    if (!confirmed) {
      setDraggingVisitId(null);
      setDragOverDate(null);
      return;
    }

    setError("");
    setSuccess("");

    try {
      if (visit.recurring_series_id) {
        await api.post(
          `/roster/series/${visit.recurring_series_id}/move-occurrence`,
          {
            occurrence_date:
              visit.recurring_occurrence_date ||
              visit.date,
            target_date: targetDate,
            reason: "Moved by drag and drop on Roster Board",
          }
        );

        setSuccess(
          `Recurring occurrence moved to ${targetDate}. The remaining series was not changed.`
        );
      } else {
        const payload = {
          client_id: visit.client_id,
          staff_id: visit.staff_id,
          date: targetDate,
          start_time: String(
            visit.start_time || "09:00"
          ).slice(0, 5),
          end_time: String(
            visit.end_time || "10:00"
          ).slice(0, 5),
          service_type: visit.service_type || "",
          address: visit.address || "",
          notes: visit.notes || null,
          geofence_radius_m:
            Number(visit.geofence_radius_m) || 100,
          status: visit.status || "upcoming",
        };

        await api.put(`/visits/${visit.id}`, payload);
        await api.post(`/roster/visits/${visit.id}/draft`);

        setSuccess(
          `Visit moved to ${targetDate} and returned to Draft. Publish the relevant day or week when ready.`
        );
      }

      await loadRoster();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to move the rostered visit."
      );
    } finally {
      setDraggingVisitId(null);
      setDragOverDate(null);
    }
  };

  const resetDragState = useCallback(() => {
    setDraggingVisitId(null);
    setDragOverDate(null);
    setDragOverWorkerId(null);
  }, []);

  useEffect(() => {
    const reset = () => resetDragState();

    window.addEventListener("dragend", reset);
    window.addEventListener("drop", reset);

    return () => {
      window.removeEventListener("dragend", reset);
      window.removeEventListener("drop", reset);
    };
  }, [resetDragState]);

  const handleVisitDragStart = (event, visit) => {
    if (
      visit.status === "completed" ||
      visit.status === "in_progress" ||
      visit.status === "clocked_on" ||
      visit.status === "clocked_off"
    ) {
      event.preventDefault();
      setError(
        "Completed or active visits cannot be moved by drag and drop."
      );
      return;
    }

    resetDragState();
    event.dataTransfer.clearData();
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(
      "application/x-cruzzn-visit-id",
      visit.id
    );
    event.dataTransfer.setData("text/plain", visit.id);

    setDraggingVisitId(visit.id);
    setOpenSeriesMenuId(null);
  };

  const handleVisitDragEnd = (event) => {
    try {
      event?.dataTransfer?.clearData();
    } catch (clearError) {
      // Some browsers lock the drag store after dragend.
    }

    resetDragState();
  };

  const handleDayDragOver = (event, date) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    setDragOverDate(date);
  };

  const handleDayDragLeave = (event, date) => {
    if (
      event.currentTarget.contains(
        event.relatedTarget
      )
    ) {
      return;
    }

    setDragOverDate((current) =>
      current === date ? null : current
    );
  };

  const handleDayDrop = async (event, date) => {
    event.preventDefault();
    event.stopPropagation();

    const visitId =
      event.dataTransfer.getData(
        "application/x-cruzzn-visit-id"
      ) ||
      event.dataTransfer.getData("text/plain");

    const visit = visits.find(
      (item) => item.id === visitId
    );

    try {
      event.dataTransfer.clearData();
    } catch (clearError) {
      // Browser may lock the drag store after reading.
    }

    resetDragState();

    if (!visit) {
      setError("The dragged visit could not be found.");
      return;
    }

    await moveVisitToDate(visit, date);
  };

  const moveVisitInStaffPlanner = async (
    visit,
    targetDate,
    targetStaffId
  ) => {
    if (!visit || !targetDate || !targetStaffId) {
      setError(
        "The shift could not be moved because its target was incomplete."
      );
      return;
    }

    if (
      [
        "completed",
        "in_progress",
        "clocked_on",
        "clocked_off",
      ].includes(visit.status)
    ) {
      setError(
        "Completed or active visits cannot be moved."
      );
      return;
    }

    const dateChanged = targetDate !== visit.date;
    const workerChanged =
      targetStaffId !== visit.staff_id;

    if (!dateChanged && !workerChanged) {
      return;
    }

    const targetWorker = staff.find(
      (worker) => worker.id === targetStaffId
    );

    if (!targetWorker) {
      setError(
        "The target support worker could not be found."
      );
      return;
    }

    const targetWorkerName = getPersonName(
      targetWorker,
      "Support worker"
    );

    const participantName =
      visit.client_name ||
      participantLookup[visit.client_id] ||
      "Participant";

    const targetConflict = weekVisits.some(
      (other) =>
        other.id !== visit.id &&
        other.status !== "cancelled" &&
        other.staff_id === targetStaffId &&
        other.date === targetDate &&
        visitsOverlap(
          {
            ...visit,
            date: targetDate,
            staff_id: targetStaffId,
          },
          other
        )
    );

    const conflictWarning = targetConflict
      ? "\n\nWARNING: This creates a worker overlap."
      : "";

    const confirmed = window.confirm(
      `Move ${participantName} to ${targetWorkerName} on ${targetDate}?`
      + `\n\nTime: ${String(
        visit.start_time || ""
      ).slice(0, 5)}–${String(
        visit.end_time || ""
      ).slice(0, 5)}`
      + conflictWarning
      + "\n\nThe moved shift will be returned to Draft."
    );

    if (!confirmed) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      if (visit.recurring_series_id) {
        if (dateChanged && workerChanged) {
          await api.post(
            `/roster/series/${visit.recurring_series_id}/move-occurrence`,
            {
              occurrence_date:
                visit.recurring_occurrence_date ||
                visit.date,
              target_date: targetDate,
              reason:
                "Moved in Project Atlas Staff Planner",
            }
          );

          await api.post(
            `/roster/series/${visit.recurring_series_id}/replace-worker`,
            {
              occurrence_date: targetDate,
              replacement_staff_id: targetStaffId,
              reason:
                "Reassigned in Project Atlas Staff Planner",
            }
          );
        } else if (dateChanged) {
          await api.post(
            `/roster/series/${visit.recurring_series_id}/move-occurrence`,
            {
              occurrence_date:
                visit.recurring_occurrence_date ||
                visit.date,
              target_date: targetDate,
              reason:
                "Moved in Project Atlas Staff Planner",
            }
          );
        } else {
          await api.post(
            `/roster/series/${visit.recurring_series_id}/replace-worker`,
            {
              occurrence_date:
                visit.recurring_occurrence_date ||
                visit.date,
              replacement_staff_id: targetStaffId,
              reason:
                "Reassigned in Project Atlas Staff Planner",
            }
          );
        }

        setSuccess(
          `Recurring occurrence moved to ${targetWorkerName} on ${targetDate}.`
        );
      } else {
        await api.put(`/visits/${visit.id}`, {
          client_id: visit.client_id,
          staff_id: targetStaffId,
          date: targetDate,
          start_time: String(
            visit.start_time || "09:00"
          ).slice(0, 5),
          end_time: String(
            visit.end_time || "10:00"
          ).slice(0, 5),
          service_type: visit.service_type || "",
          address: visit.address || "",
          notes: visit.notes || null,
          geofence_radius_m:
            Number(visit.geofence_radius_m) || 100,
          status: visit.status || "upcoming",
        });

        await api.post(
          `/roster/visits/${visit.id}/draft`
        );

        setSuccess(
          `${participantName} moved to ${targetWorkerName} on ${targetDate} and returned to Draft.`
        );
      }

      await loadRoster();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to move the shift in the staff planner."
      );
    } finally {
      resetDragState();
    }
  };

  const deleteVisit = async (visit) => {
    if (visit.recurring_series_id) {
      await skipRecurringOccurrence(visit);
      return;
    }

    const participant =
      visit.client_name ||
      participantLookup[visit.client_id] ||
      "this participant";

    const isPublished =
      visit.publication_status === "published" &&
      visit.status !== "draft";

    const warning = isPublished
      ? `This visit has already been published to the assigned worker.\n\n`
      : "";

    const confirmed = window.confirm(
      `${warning}Delete the ${visit.start_time}–${visit.end_time} visit for ${participant}?`
    );

    if (!confirmed) {
      return;
    }

    setError("");
    setSuccess("");

    try {
      await api.delete(`/visits/${visit.id}`);
      setSuccess("Rostered visit deleted.");
      await loadRoster();
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail ||
          requestError?.message ||
          "Unable to delete the rostered visit."
      );
    }
  };

  const isCurrentWeek =
    weekStartString ===
    localDateString(startOfWeek(new Date()));

  return (
    <div
      className="space-y-5 p-4 md:p-6"
      data-testid="project-atlas-workforce-planner-v40a"
    >
      <section className="rounded-2xl border bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <CalendarDays className="h-7 w-7 text-emerald-600" />

              <h1 className="text-2xl font-bold text-slate-900">
                Rapid X Workforce Planner
              </h1>
            </div>

            <p className="mt-1 text-sm text-slate-500">
              Project Atlas — workforce-first weekly planning and visit scheduling.
            </p>

            <p className="mt-1 text-xs font-medium text-emerald-700">
              Create and review visits as Draft, then publish the day or week to assigned workers.
            </p>

            <p className="mt-1 text-xs font-medium text-indigo-700">
              Drag a card repeatedly between days, drop it onto a worker to reassign, or use the resize handle to change its end time.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={() =>
                setCompactCards((current) => !current)
              }
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
              title={
                compactCards
                  ? "Show detailed roster cards"
                  : "Show compact roster cards"
              }
            >
              {compactCards ? (
                <Maximize2 className="h-4 w-4" />
              ) : (
                <Minimize2 className="h-4 w-4" />
              )}
              {compactCards ? "Detailed" : "Compact"}
            </button>

            <button
              type="button"
              onClick={publishWeek}
              disabled={publishing || loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-white px-4 py-2.5 text-sm font-semibold text-emerald-700 shadow-sm hover:bg-emerald-50 disabled:opacity-50"
            >
              <Send
                className={`h-4 w-4 ${
                  publishing ? "animate-pulse" : ""
                }`}
              />
              Publish Week
            </button>

            <button
              type="button"
              onClick={() => openCreate()}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
            >
              <Plus className="h-4 w-4" />
              Create Visit
            </button>
          </div>
        </div>

        <div className="mt-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() =>
                setWeekStart((current) =>
                  addWeeks(current, -1)
                )
              }
              className="rounded-lg border p-2 text-slate-600 hover:bg-slate-50"
              aria-label="Previous week"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            <button
              type="button"
              onClick={() =>
                setWeekStart(startOfWeek(new Date()))
              }
              disabled={isCurrentWeek}
              className="rounded-lg border px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Today
            </button>

            <button
              type="button"
              onClick={() =>
                setWeekStart((current) =>
                  addWeeks(current, 1)
                )
              }
              className="rounded-lg border p-2 text-slate-600 hover:bg-slate-50"
              aria-label="Next week"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            <div className="ml-2">
              <p className="font-bold text-slate-900">
                {formatWeekRange(weekStart)}
              </p>

              <p className="text-xs text-slate-500">
                Australia/Sydney roster week
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={loadRoster}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw
              className={`h-4 w-4 ${
                loading ? "animate-spin" : ""
              }`}
            />
            Refresh
          </button>
        </div>
      </section>

      <section
        className="rounded-2xl border bg-white p-2 shadow-sm"
        data-testid="atlas-planner-tabs"
      >
        <div className="grid gap-2 sm:grid-cols-3">
          {[
            {
              value: "staff",
              label: "Staff",
              description: "People-first weekly timeline",
              icon: Users,
            },
            {
              value: "visits",
              label: "Visits",
              description: "Existing visit planner",
              icon: CalendarDays,
            },
            {
              value: "clients",
              label: "Clients",
              description: "Client timeline — coming next",
              icon: UserRound,
            },
          ].map((view) => {
            const Icon = view.icon;
            const selected = plannerView === view.value;

            return (
              <button
                key={view.value}
                type="button"
                onClick={() => setPlannerView(view.value)}
                className={[
                  "flex items-center gap-3 rounded-xl border px-4 py-3",
                  "text-left transition",
                  selected
                    ? "border-emerald-600 bg-emerald-600 text-white shadow-sm"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50",
                ].join(" ")}
                data-testid={`atlas-${view.value}-view-tab`}
              >
                <span
                  className={[
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                    selected ? "bg-white/15" : "bg-slate-100",
                  ].join(" ")}
                >
                  <Icon className="h-4 w-4" />
                </span>

                <span>
                  <span className="block text-sm font-black">
                    {view.label}
                  </span>
                  <span
                    className={[
                      "mt-0.5 block text-[10px]",
                      selected ? "text-emerald-50" : "text-slate-500",
                    ].join(" ")}
                  >
                    {view.description}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section
        className="rounded-2xl border border-indigo-200 bg-gradient-to-r from-indigo-50 via-white to-emerald-50 p-4 shadow-sm"
        data-testid="atlas-smart-scheduling-panel"
      >
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-700" />
              <h2 className="font-black text-slate-950">
                Smart Scheduling
              </h2>
            </div>

            <p className="mt-1 text-sm text-slate-600">
              Auto-assign unfilled visits using overlap, 30-minute
              travel-buffer and 38-hour workload checks.
            </p>
          </div>

          <button
            type="button"
            onClick={runOneClickAutoAssign}
            disabled={
              smartScheduling ||
              loading ||
              smartSchedulingInsights.unfilled_count === 0
            }
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-700 px-4 py-2.5 text-sm font-black text-white shadow-sm hover:bg-indigo-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Sparkles
              className={`h-4 w-4 ${
                smartScheduling
                  ? "animate-pulse"
                  : ""
              }`}
            />
            {smartScheduling
              ? "Auto-assigning…"
              : "Auto Assign Unfilled"}
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            {
              label: "Unfilled visits",
              value:
                smartSchedulingInsights
                  .unfilled_count,
              tone:
                smartSchedulingInsights
                  .unfilled_count
                  ? "border-amber-300 bg-amber-50 text-amber-950"
                  : "border-emerald-200 bg-emerald-50 text-emerald-950",
              icon: UserRound,
            },
            {
              label: "Double bookings",
              value:
                smartSchedulingInsights
                  .double_booked_count,
              tone:
                smartSchedulingInsights
                  .double_booked_count
                  ? "border-red-300 bg-red-50 text-red-950"
                  : "border-emerald-200 bg-emerald-50 text-emerald-950",
              icon: AlertTriangle,
            },
            {
              label: "Travel warnings",
              value:
                smartSchedulingInsights
                  .travel_warning_count,
              tone:
                smartSchedulingInsights
                  .travel_warning_count
                  ? "border-orange-300 bg-orange-50 text-orange-950"
                  : "border-emerald-200 bg-emerald-50 text-emerald-950",
              icon: Route,
            },
            {
              label: "Overtime risks",
              value:
                smartSchedulingInsights
                  .overtime_count,
              tone:
                smartSchedulingInsights
                  .overtime_count
                  ? "border-violet-300 bg-violet-50 text-violet-950"
                  : "border-emerald-200 bg-emerald-50 text-emerald-950",
              icon: Clock3,
            },
          ].map((item) => {
            const Icon = item.icon;

            return (
              <div
                key={item.label}
                className={`rounded-xl border p-3 ${item.tone}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase tracking-wide">
                    {item.label}
                  </span>
                  <Icon className="h-4 w-4 opacity-70" />
                </div>
                <p className="mt-1 text-2xl font-black">
                  {item.value}
                </p>
              </div>
            );
          })}
        </div>

        {smartSchedulingResult && (
          <div className="mt-3 rounded-xl border border-indigo-200 bg-white px-4 py-3 text-sm font-semibold text-indigo-900">
            {smartSchedulingResult.message}
          </div>
        )}

        {smartSchedulingInsights.unfilled_count > 0 && (
          <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-950">
            Unfilled visits are highlighted in the Visit View and
            excluded from publishing to staff until assigned.
          </div>
        )}
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-8">
        {[
          ["Total visits", summary.total, CalendarDays],
          ["Drafts", summary.drafts, Pencil],
          ["Upcoming", summary.upcoming, Clock3],
          ["In progress", summary.inProgress, UserRound],
          ["Completed", summary.completed, CheckCircle2],
          ["Roster hours", formatDuration(summary.hours), Clock3],
          ["Workers", summary.workers, Users],
          ["Conflicts", summary.conflicts, AlertTriangle],
        ].map(([label, value, Icon]) => (
          <div
            key={label}
            className="rounded-xl border bg-white px-4 py-3 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {label}
              </span>

              <Icon className="h-4 w-4 text-slate-400" />
            </div>

            <div className="mt-1 text-2xl font-bold text-slate-900">
              {value}
            </div>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-1 text-xs font-bold uppercase tracking-wide text-slate-500">
            Service colours
          </span>

          {SERVICE_TYPES.map((service) => {
            const palette = getServiceCardStyle(service);

            return (
              <span
                key={service}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold ${palette.badge}`}
              >
                <span
                  className={`h-2 w-2 rounded-full ${palette.accent}`}
                />
                {service}
              </span>
            );
          })}
        </div>
      </section>

      <section className="rounded-2xl border bg-white p-4 shadow-sm">
        <div className="grid gap-3 lg:grid-cols-[minmax(260px,1fr)_220px_190px_auto]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />

            <input
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder="Search participant, worker, service or address"
              className="w-full rounded-xl border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
          </div>

          <div className="relative">
            <UserRound className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />

            <select
              value={staffFilter}
              onChange={(event) =>
                setStaffFilter(event.target.value)
              }
              className="w-full appearance-none rounded-xl border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            >
              <option value="">All workers</option>

              {staff.map((worker) => (
                <option key={worker.id} value={worker.id}>
                  {getPersonName(worker, "Worker")}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <Filter className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />

            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value)
              }
              className="w-full appearance-none rounded-xl border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            >
              <option value="">All statuses</option>

              {STATUS_OPTIONS.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={() => {
              setSearch("");
              setStaffFilter("");
              setStatusFilter("");
            }}
            className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
          >
            Clear
          </button>
        </div>
      </section>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {success && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {success}
        </div>
      )}

      {draggingVisitId && (
        <section className="rounded-2xl border border-indigo-200 bg-gradient-to-r from-indigo-50 via-white to-sky-50 p-4 shadow-sm">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-700" />
            <div>
              <h2 className="font-bold text-indigo-950">
                Drop onto a worker to reassign
              </h2>
              <p className="text-xs text-indigo-700">
                Recurring visits change this occurrence only.
              </p>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            {staff.map((worker) => (
              <button
                key={worker.id}
                type="button"
                onDragOver={(event) =>
                  handleWorkerDragOver(
                    event,
                    worker.id
                  )
                }
                onDragLeave={() =>
                  setDragOverWorkerId((current) =>
                    current === worker.id
                      ? null
                      : current
                  )
                }
                onDrop={(event) =>
                  handleWorkerDrop(event, worker)
                }
                className={`rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                  dragOverWorkerId === worker.id
                    ? "scale-105 border-indigo-600 bg-indigo-600 text-white shadow-lg"
                    : "border-indigo-200 bg-white text-indigo-800 hover:bg-indigo-50"
                }`}
              >
                {getPersonName(worker, "Worker")}
              </button>
            ))}
          </div>
        </section>
      )}

      {loading ? (
        <section className="rounded-2xl border bg-white p-12 text-center shadow-sm">
          <RefreshCw className="mx-auto h-8 w-8 animate-spin text-emerald-600" />
          <p className="mt-3 text-sm text-slate-500">
            Loading weekly roster…
          </p>
        </section>
      ) : plannerView === "staff" ? (
        <StaffPlannerView
          staff={staff}
          weekDays={weekDays}
          weekVisits={weekVisits}
          participantLookup={participantLookup}
          conflictsByVisitId={conflictsByVisitId}
          staffFilter={staffFilter}
          onOpenVisit={openEdit}
          onCreateVisit={openCreate}
          onMoveVisit={moveVisitInStaffPlanner}
          onDragStart={handleVisitDragStart}
          onDragEnd={handleVisitDragEnd}
          draggingVisitId={draggingVisitId}
        />
      ) : plannerView === "clients" ? (
        <section
          className="rounded-2xl border bg-white p-12 text-center shadow-sm"
          data-testid="atlas-client-view-placeholder"
        >
          <UserRound className="mx-auto h-10 w-10 text-emerald-600" />
          <h2 className="mt-4 text-xl font-black text-slate-900">
            Client Timeline
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500">
            Project Atlas V4.0B will provide a client-first weekly view
            using the same roster data. The existing Visit View remains
            fully operational now.
          </p>
          <button
            type="button"
            onClick={() => setPlannerView("visits")}
            className="mt-5 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700"
          >
            Open Visit View
          </button>
        </section>
      ) : (
        <section className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="overflow-x-auto">
            <div className="grid min-w-[1280px] grid-cols-7 divide-x">
              {weekDays.map((day) => {
                const date = localDateString(day);
                const dayVisits = visitsByDate[date] || [];
                const isToday =
                  date === localDateString(new Date());

                const dayMinutes = dayVisits
                  .filter(
                    (visit) => visit.status !== "cancelled"
                  )
                  .reduce(
                    (total, visit) =>
                      total +
                      durationMinutes(
                        visit.start_time,
                        visit.end_time
                      ),
                    0
                  );

                return (
                  <div
                    key={date}
                    onDragOver={(event) =>
                      handleDayDragOver(event, date)
                    }
                    onDragLeave={(event) =>
                      handleDayDragLeave(event, date)
                    }
                    onDrop={(event) =>
                      handleDayDrop(event, date)
                    }
                    className={`min-h-[620px] transition ${
                      dragOverDate === date
                        ? "bg-gradient-to-b from-emerald-50 via-cyan-50/80 to-white ring-4 ring-inset ring-emerald-300 shadow-inner"
                        : ""
                    }`}
                  >
                    <div
                      className={`sticky top-0 z-10 border-b px-3 py-3 ${
                        isToday
                          ? "bg-emerald-50"
                          : "bg-slate-50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h2
                            className={`font-bold ${
                              isToday
                                ? "text-emerald-800"
                                : "text-slate-900"
                            }`}
                          >
                            {formatDayHeading(day)}
                          </h2>

                          <p className="mt-0.5 text-xs text-slate-500">
                            {dayVisits.length} visit
                            {dayVisits.length === 1 ? "" : "s"} ·{" "}
                            {formatDuration(dayMinutes)}
                          </p>
                        </div>

                        <div className="flex items-center gap-1">
                          {dayVisits.length > 0 && (
                            <button
                              type="button"
                              onClick={() => publishDay(date)}
                              disabled={publishing}
                              className="rounded-lg border bg-white p-1.5 text-emerald-700 hover:border-emerald-400 hover:bg-emerald-50 disabled:opacity-50"
                              title="Publish day"
                            >
                              <Send className="h-4 w-4" />
                            </button>
                          )}

                          <button
                            type="button"
                            onClick={() => openCreate(date)}
                            className="rounded-lg border bg-white p-1.5 text-slate-500 hover:border-emerald-300 hover:text-emerald-700"
                            title="Add visit"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3 p-3">
                      {dayVisits.length === 0 ? (
                        <button
                          type="button"
                          onClick={() => openCreate(date)}
                          className="flex min-h-[110px] w-full flex-col items-center justify-center rounded-xl border border-dashed text-slate-400 hover:border-emerald-300 hover:bg-emerald-50/50 hover:text-emerald-700"
                        >
                          <Plus className="h-5 w-5" />
                          <span className="mt-1 text-xs font-medium">
                            Add visit
                          </span>
                        </button>
                      ) : (
                        dayVisits.map((visit) => {
                          const style = getStatusStyle(
                            visit.status
                          );

                          const serviceStyle =
                            getServiceCardStyle(
                              visit.service_type
                            );

                          const conflict =
                            conflictsByVisitId.has(visit.id);

                          const participantName =
                            visit.client_name ||
                            participantLookup[
                              visit.client_id
                            ] ||
                            "Participant";

                          const workerName =
                            visit.staff_name ||
                            staffLookup[visit.staff_id] ||
                            "Unassigned";

                          return (
                            <article
                              key={`${visit.id}-${visit.date}-${visit.staff_id}-${visit.start_time}-${visit.end_time}`}
                              draggable={
                                ![
                                  "completed",
                                  "in_progress",
                                  "clocked_on",
                                  "clocked_off",
                                ].includes(visit.status)
                              }
                              onDragStart={(event) =>
                                handleVisitDragStart(
                                  event,
                                  visit
                                )
                              }
                              onDragEnd={(event) =>
                                handleVisitDragEnd(event)
                              }
                              className={`group relative overflow-visible rounded-xl border border-l-[6px] shadow-sm transition hover:-translate-y-0.5 hover:shadow-xl ${
                                compactCards ? "p-2.5" : "min-h-[250px] p-4"
                              } ${
                                !visit.staff_id
                                  ? "ring-4 ring-amber-300 bg-amber-50"
                                  : ""
                              } ${style.card} ${serviceStyle.card} ${
                                conflict
                                  ? "ring-2 ring-red-200"
                                  : ""
                              } ${
                                draggingVisitId === visit.id
                                  ? "cursor-grabbing opacity-50"
                                  : "cursor-grab"
                              }`}
                            >
                              <div
                                className={`absolute inset-x-3 top-0 h-1 rounded-b-full ${serviceStyle.accent}`}
                                aria-hidden="true"
                              />

                              {draggingVisitId === visit.id && (
                                <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-slate-950/55 text-sm font-bold text-white backdrop-blur-[1px]">
                                  Moving visit…
                                </div>
                              )}

                              <div className="flex items-start justify-between gap-2 pt-1">
                                <div
                                  className={`flex items-center gap-1.5 font-black tracking-tight ${serviceStyle.header} ${
                                    compactCards
                                      ? "text-sm"
                                      : "text-lg"
                                  }`}
                                >
                                  <GripVertical
                                    className="h-4 w-4 text-slate-400"
                                    aria-hidden="true"
                                  />
                                  <Clock3 className="h-4 w-4 opacity-60" />
                                  {String(
                                    visit.start_time || ""
                                  ).slice(0, 5)}
                                  –
                                  {resizeState?.visit?.id ===
                                  visit.id
                                    ? resizeState.previewEndTime
                                    : String(
                                        visit.end_time || ""
                                      ).slice(0, 5)}
                                </div>

                                <span
                                  className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${style.badge}`}
                                >
                                  {getStatusLabel(
                                    visit.status
                                  )}
                                </span>
                              </div>

                              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] font-semibold">
                                {visit.publication_status === "draft" ||
                                visit.status === "draft" ? (
                                  <span className="rounded-md bg-violet-50 px-2 py-1 text-violet-700">
                                    Not published
                                  </span>
                                ) : (
                                  <span className="rounded-md bg-emerald-50 px-2 py-1 text-emerald-700">
                                    Published
                                  </span>
                                )}

                                {visit.recurring_series_id && (
                                  <span
                                    className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-1 text-indigo-700"
                                    title="Generated from a recurring roster series"
                                  >
                                    <Repeat2 className="h-3 w-3" />
                                    Recurring
                                  </span>
                                )}
                              </div>

                              {!visit.staff_id && (
                                <div className="mt-2 flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-100 px-2 py-1.5 text-xs font-extrabold text-amber-900 shadow-sm">
                                  <AlertTriangle className="h-4 w-4" />
                                  Unfilled visit — assign a worker
                                </div>
                              )}

                              {conflict && (
                                <div className="mt-2 flex items-center gap-1.5 rounded-lg border border-red-300 bg-red-100 px-2 py-1.5 text-xs font-extrabold text-red-800 shadow-sm">
                                  <AlertTriangle className="h-4 w-4" />
                                  Worker overlap detected
                                </div>
                              )}

                              <div className={compactCards ? "mt-2" : "mt-3"}>
                                <p
                                  className={`line-clamp-2 font-extrabold tracking-tight text-slate-950 ${
                                    compactCards
                                      ? "text-sm"
                                      : "text-base"
                                  }`}
                                >
                                  {participantName}
                                </p>

                                <div
                                  className={`flex items-center gap-2 ${
                                    compactCards ? "mt-1" : "mt-2"
                                  }`}
                                >
                                  <div
                                    className={`flex shrink-0 items-center justify-center rounded-full bg-slate-900 font-bold text-white shadow-sm ${
                                      compactCards
                                        ? "h-6 w-6 text-[10px]"
                                        : "h-8 w-8 text-xs"
                                    }`}
                                    title={workerName}
                                  >
                                    {getInitials(workerName)}
                                  </div>

                                  <div className="min-w-0">
                                    <p
                                      className={`truncate font-semibold text-slate-700 ${
                                        compactCards
                                          ? "text-[11px]"
                                          : "text-xs"
                                      }`}
                                    >
                                      {workerName}
                                    </p>

                                    {!compactCards && (
                                      <p className="text-[10px] uppercase tracking-wide text-slate-400">
                                        Assigned worker
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {visit.service_type && (
                                <div className="mt-2">
                                  <span
                                    className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-bold ${serviceStyle.badge}`}
                                  >
                                    <Palette className="h-3 w-3" />
                                    {visit.service_type}
                                  </span>
                                </div>
                              )}

                              {!compactCards && visit.address && (
                                <div className="mt-2 flex items-start gap-1.5 text-[11px] leading-4 text-slate-500">
                                  <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                                  <span className="line-clamp-2">
                                    {visit.address}
                                  </span>
                                </div>
                              )}

                              <div className="mt-3 flex items-center justify-between border-t pt-2">
                                <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2 py-1 text-[10px] font-bold text-white shadow-sm">
                                  <Clock3 className="h-3 w-3" />
                                  {formatDuration(
                                    durationMinutes(
                                      visit.start_time,
                                      visit.end_time
                                    )
                                  )}
                                </span>

                                <div className="flex gap-1">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      duplicateVisit(visit)
                                    }
                                    className="rounded-md p-1.5 text-slate-400 hover:bg-indigo-50 hover:text-indigo-700"
                                    title="Duplicate visit"
                                  >
                                    <Copy className="h-3.5 w-3.5" />
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      visit.recurring_series_id
                                        ? openRecurringEdit(visit)
                                        : openEdit(visit)
                                    }
                                    className="rounded-md p-1.5 text-slate-400 hover:bg-blue-50 hover:text-blue-700"
                                    title={
                                      visit.recurring_series_id
                                        ? "Edit recurring visit"
                                        : "Edit visit"
                                    }
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>

                                  {visit.recurring_series_id && (
                                    <div className="relative">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setOpenSeriesMenuId(
                                            (current) =>
                                              current === visit.id
                                                ? null
                                                : visit.id
                                          )
                                        }
                                        className="rounded-md p-1.5 text-slate-400 hover:bg-indigo-50 hover:text-indigo-700"
                                        title="Recurring series actions"
                                      >
                                        <MoreVertical className="h-3.5 w-3.5" />
                                      </button>

                                      {openSeriesMenuId === visit.id && (
                                        <div className="absolute bottom-8 right-0 z-30 w-56 overflow-hidden rounded-xl border bg-white py-1 shadow-xl">
                                          <button type="button" onClick={() => skipRecurringOccurrence(visit)} className="block w-full px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50">
                                            Skip this occurrence
                                          </button>
                                          <button type="button" onClick={() => changeRecurringTime(visit)} className="block w-full px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50">
                                            Change time once
                                          </button>
                                          <button type="button" onClick={() => replaceRecurringWorker(visit)} className="block w-full px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50">
                                            Replace worker once
                                          </button>
                                          <button type="button" onClick={() => toggleRecurringPause(visit)} className="block w-full px-3 py-2 text-left text-xs font-semibold text-slate-700 hover:bg-slate-50">
                                            Pause / resume series
                                          </button>
                                          <button type="button" onClick={() => ceaseRecurringSeries(visit)} className="block w-full px-3 py-2 text-left text-xs font-semibold text-red-700 hover:bg-red-50">
                                            Cease recurring series
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  <button
                                    type="button"
                                    onClick={() =>
                                      deleteVisit(visit)
                                    }
                                    className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-700"
                                    title={
                                      visit.recurring_series_id
                                        ? "Skip recurring occurrence"
                                        : "Delete visit"
                                    }
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </div>
                              </div>

                              {!compactCards && ![
                                "completed",
                                "in_progress",
                                "clocked_on",
                                "clocked_off",
                              ].includes(visit.status) && (
                                <div className="mt-2 border-t border-slate-200/70 pt-2">
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                      Resize end time
                                    </span>

                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        draggable={false}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          updateVisitEndTime(
                                            visit,
                                            shiftTime(
                                              visit.end_time,
                                              -15
                                            )
                                          );
                                        }}
                                        className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:bg-slate-50"
                                        title="End 15 minutes earlier"
                                      >
                                        <Minus className="h-3 w-3" />
                                      </button>

                                      <button
                                        type="button"
                                        draggable={false}
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          updateVisitEndTime(
                                            visit,
                                            shiftTime(
                                              visit.end_time,
                                              15
                                            )
                                          );
                                        }}
                                        className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:bg-slate-50"
                                        title="End 15 minutes later"
                                      >
                                        <Plus className="h-3 w-3" />
                                      </button>
                                    </div>
                                  </div>

                                  <button
                                    type="button"
                                    draggable={false}
                                    onPointerDown={(event) =>
                                      handleResizePointerDown(
                                        event,
                                        visit
                                      )
                                    }
                                    className="mt-2 flex h-3 w-full cursor-ns-resize items-center justify-center rounded-full bg-slate-200/80 hover:bg-indigo-200"
                                    title="Drag vertically to resize end time in 15-minute steps"
                                  >
                                    <span className="h-1 w-10 rounded-full bg-slate-400" />
                                  </button>
                                </div>
                              )}
                            </article>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <RosterModal
        open={modalOpen}
        form={form}
        setForm={setForm}
        participants={participants}
        staff={staff}
        visits={visits}
        saving={saving}
        onClose={() => setModalOpen(false)}
        onSubmit={submitVisit}
      />
    </div>
  );
}
